// S2 product-detail tab strip + panels (Module 01 §4), on the revision-6
// register. The strip is the §3.10 `NavTabs` primitive — a <nav> of buttons
// with `aria-current`, underlined in the accent — carrying the `detail-tabs`
// class the certified M01-S03/S12/S13 walkthroughs select on. The panel
// wrappers keep their `…-tab` class names for the same reason (M01-S12 reads
// `.uom-tab` / `.where-used-tab`; M01-S13 reads `.cost-tab`).
//
// Each panel fetches its own slice via a foundation hook and renders the
// explicit §3.9 loading / empty / error states — errors always carry a retry
// AND the request id. Cost surfaces are redaction-aware: the Cost tab is only
// offered when the product carries a cost field.

import { formatTimestampDate } from "../../../../../lib/datetime";
import { shortId as shortIdTail } from "../../../../../lib/short-id";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Card } from "../../../../../components/card";
import { Button } from "../../../../../components/button";
import { Banner } from "../../../../../components/toast";
import { NavTabs } from "../../../../../components/tabs";
import { DataTable, type DataTableColumn } from "../../../../../components/data-table";
import { CardSkeleton, ErrorState } from "../../../../../components/states";
import { Select } from "../../../../../components/select";
import { StatusBadge } from "../../../../../components/status-badge";
import { PRODUCT_TRACKING, type Product } from "../../../../../lib/api";
import { MAKE_OR_BUY } from "./product-filters";
import { requestIdOf, type FieldError, RequestError } from "../../../../../lib/request";
import { AuditTrailLink } from "../../../audit/components/audit-trail-link";
import { CostValue } from "../../components/cost-display";
import { EffectivityTimeline } from "../../components/effectivity-timeline";
import { SchemaAttributeForm } from "../../components/schema-form";
import {
  useAttributeSchemas,
  useChangeLog,
  useFacilities,
  useProductBoms,
  useProductConversions,
  useProductRoutings,
  useUoms,
  useUpdateProduct,
  useWhereUsed,
} from "../../hooks/catalogue-queries";
import { useCataloguePermissions } from "../../hooks/permissions";

type AttributeSchema = components["schemas"]["AttributeSchema"];
type Conversion = components["schemas"]["UomConversion"];

export type TabKey =
  "details" | "attributes" | "uom" | "boms" | "routings" | "whereUsed" | "cost" | "history";

const BASE_TABS: TabKey[] = ["details", "attributes", "uom", "boms", "routings", "whereUsed"];
const ALL_TABS: readonly string[] = [...BASE_TABS, "cost", "history"];

/**
 * A deep link may name the tab to open in the URL hash
 * (`/catalogue/products/{id}#uom`). Module 06 §4.10's "conversion factors are
 * edited in Module 01" pointer lands exactly there, and a link that drops the
 * user on the wrong tab is a link that has not really arrived. The hash is used
 * only as an INITIAL value — the strip stays the source of truth afterwards —
 * and an unknown hash falls back to the default tab.
 */
function initialTabFromHash(): TabKey {
  const key = window.location.hash.replace(/^#/u, "");
  return ALL_TABS.includes(key) ? (key as TabKey) : "details";
}

export function ProductDetailTabs({
  product,
  /* §1 rule 1 — ONE primary per view. A panel with unsaved edits owns the
   * primary (saving is the next step; you cannot usefully activate a product
   * whose edits are still in the browser), and the page head's lifecycle button
   * demotes while that is true. The signal is lifted here rather than guessed
   * in two places, and it resets on a tab switch — the panel unmounts, so its
   * edits are gone with it. */
  onDirtyChange,
}: {
  product: Product;
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>(initialTabFromHash);

  const costReadable = product.standard_cost_minor !== undefined;
  const tabs: TabKey[] = costReadable
    ? [...BASE_TABS, "cost", "history"]
    : [...BASE_TABS, "history"];
  const current = tabs.includes(activeTab) ? activeTab : "details";

  return (
    <>
      <NavTabs
        className="detail-tabs"
        label={t("catalogue.productDetail.tabsLabel")}
        active={current}
        onSelect={(id) => {
          setActiveTab(id as TabKey);
          // The outgoing panel unmounts; nothing is pending any more.
          onDirtyChange?.(false);
        }}
        tabs={tabs.map((tab) => ({
          id: tab,
          label: t(`catalogue.productDetail.tabs.${tab}`),
          "data-testid": `product-tab-${tab}`,
        }))}
      />

      <div className="detail-panel" id="product-detail-panel">
        {current === "details" ? (
          <DetailsTab product={product} onDirtyChange={onDirtyChange} />
        ) : null}
        {current === "attributes" ? (
          <AttributesTab product={product} onDirtyChange={onDirtyChange} />
        ) : null}
        {current === "uom" ? <UomTab product={product} /> : null}
        {current === "boms" ? <BomsTab product={product} /> : null}
        {current === "routings" ? <RoutingsTab product={product} /> : null}
        {current === "whereUsed" ? <WhereUsedTab product={product} /> : null}
        {current === "cost" && costReadable ? <CostTab product={product} /> : null}
        {current === "history" ? <HistoryTab product={product} /> : null}
      </div>
    </>
  );
}

function useUomCodeMap() {
  const uoms = useUoms();
  const map = new Map<string, string>();
  for (const u of uoms.data?.items ?? []) map.set(u.id, u.code);
  return map;
}

/**
 * facility id → facility NAME. A uuid is a database fact, never a UI fact: the
 * Routings panel used to read "Facility: 0197c5db-b600-729d-…", which tells a
 * planner nothing about which plant the routing belongs to. Falls back to the
 * id only while the reference list is still loading or if a routing points at a
 * facility this role cannot see — visibly wrong beats invisibly wrong.
 */
function useFacilityNameMap() {
  const facilities = useFacilities();
  const map = new Map<string, string>();
  for (const f of facilities.data?.items ?? []) map.set(f.id, f.name);
  return map;
}

function TabError({
  query,
  titleKey,
}: {
  query: { error: unknown; refetch: () => unknown };
  titleKey: string;
}) {
  const { t } = useTranslation();
  const message = query.error instanceof Error ? query.error.message : undefined;
  return (
    <ErrorState
      title={t(titleKey)}
      detail={message}
      retryLabel={t("catalogue.state.retry")}
      onRetry={() => void query.refetch()}
      requestId={requestIdOf(query.error)}
      requestIdLabel={t("catalogue.state.requestId")}
    />
  );
}

// ---- Details (master data) --------------------------------------------------

/** `null` for a blank number field, `undefined` for "leave as-is" is not needed
 * here — the form always sends the full editable set, so blank means null. */
function numberOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * S2 Details — the product's own master data.
 *
 * `PATCH /v1/catalogue/products/{id}` has always accepted eleven fields; the UI
 * exposed exactly ONE of them (`attributes`). A planner who mistyped a SKU or a
 * product name had no way to correct it anywhere in the product, and S1 shipped
 * a "Make or buy" FILTER for a value no screen could set. Spec 01 §7 lists
 * "Create/edit product" for three roles; §4's S2 row never described the form,
 * so the slice inherited a spec gap and did not close it. This closes it.
 *
 * `kind` and `base_uom_id` are deliberately NOT here: the API does not accept
 * them on PATCH (changing a product's unit after stock and BOMs reference it is
 * a migration, not an edit), so they render as read-only facts rather than as
 * controls that would silently do nothing.
 */
export function DetailsTab({
  product,
  onDirtyChange,
}: {
  product: Product;
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
}) {
  const { t } = useTranslation();
  const update = useUpdateProduct(product.id);
  const perms = useCataloguePermissions();
  const uomCode = useUomCodeMap();
  const facilities = useFacilities();
  const canWrite = perms.has("catalogue.product.write");

  const [form, setForm] = useState({
    sku: product.sku,
    name: product.name,
    description: product.description ?? "",
    make_or_buy: product.make_or_buy,
    tracking: product.tracking,
    assembly_scrap_pct: String(product.assembly_scrap_pct),
    purchase_lead_days:
      product.purchase_lead_days === null ? "" : String(product.purchase_lead_days),
    manufacture_lead_days:
      product.manufacture_lead_days === null ? "" : String(product.manufacture_lead_days),
    price_unit_qty: String(product.price_unit_qty),
    default_facility_id: product.default_facility_id ?? "",
  });
  const [serverErrors, setServerErrors] = useState<readonly FieldError[]>([]);
  const [notice, setNotice] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  /* The SKU is immutable once the product leaves draft — the service answers
   * 409 SKU_IMMUTABLE. Disabling the control (and saying why) is the honest
   * rendering; offering an editable box that the server will refuse is not. */
  const skuEditable = product.status === "draft";

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setNotice(null);
    setDirty(true);
    onDirtyChange?.(true);
  }

  function save() {
    setNotice(null);
    setServerErrors([]);
    const body: components["schemas"]["ProductPatchRequest"] = {
      name: form.name.trim(),
      description: form.description.trim().length === 0 ? null : form.description.trim(),
      make_or_buy: form.make_or_buy,
      tracking: form.tracking,
      assembly_scrap_pct: Number(form.assembly_scrap_pct) || 0,
      purchase_lead_days: numberOrNull(form.purchase_lead_days),
      manufacture_lead_days: numberOrNull(form.manufacture_lead_days),
      price_unit_qty: Number(form.price_unit_qty) || 1,
      default_facility_id: form.default_facility_id.length === 0 ? null : form.default_facility_id,
    };
    if (skuEditable && form.sku.trim() !== product.sku) body.sku = form.sku.trim();

    update.mutate(body, {
      onSuccess: () => {
        setNotice({ kind: "ok", text: t("catalogue.productDetail.details.saved") });
        setDirty(false);
        onDirtyChange?.(false);
      },
      onError: (err: unknown) => {
        if (err instanceof RequestError && err.fieldErrors.length > 0) {
          setServerErrors(err.fieldErrors);
        }
        setNotice({
          kind: "bad",
          text: err instanceof Error ? err.message : t("catalogue.productDetail.details.error"),
        });
      },
    });
  }

  const disabled = update.isPending || !canWrite;

  return (
    <div className="details-tab">
      {notice !== null ? (
        <Banner
          kind={notice.kind}
          data-testid="details-result"
          dismissLabel={t("catalogue.state.dismiss")}
          onDismiss={() => {
            setNotice(null);
          }}
        >
          {notice.text}
        </Banner>
      ) : null}
      {!canWrite ? (
        <Banner kind="neutral" data-testid="details-readonly">
          {t("catalogue.productDetail.details.readOnly")}
        </Banner>
      ) : null}

      <Card title={t("catalogue.productDetail.details.title")}>
        <div className="ds-cpad">
          {/* `cat-form-detail`, not the bare add-a-row grid: this form's
              controls arrive PRE-FILLED, so its tracks are sized for values
              (catalogue.css) — the 150 px inline floor crushed the seeded SKU
              and name inside their own inputs at 1440 px. */}
          <div className="cat-form cat-form-detail">
            <div className="cat-fld">
              <label htmlFor="detail-sku">{t("catalogue.productDetail.details.sku")}</label>
              <input
                id="detail-sku"
                value={form.sku}
                disabled={disabled || !skuEditable}
                onChange={(e) => {
                  set("sku", e.target.value);
                }}
              />
              {!skuEditable ? (
                <p className="cat-hint">{t("catalogue.productDetail.details.skuLocked")}</p>
              ) : null}
            </div>

            <div className="cat-fld">
              <label htmlFor="detail-name">{t("catalogue.productDetail.details.name")}</label>
              <input
                id="detail-name"
                value={form.name}
                disabled={disabled}
                onChange={(e) => {
                  set("name", e.target.value);
                }}
                required
              />
            </div>

            <div className="cat-fld cat-form-wide">
              <label htmlFor="detail-description">
                {t("catalogue.productDetail.details.description")}
              </label>
              <textarea
                id="detail-description"
                rows={2}
                value={form.description}
                disabled={disabled}
                onChange={(e) => {
                  set("description", e.target.value);
                }}
              />
            </div>

            {/* Facts, not controls — PATCH does not accept them (see the note
                on this component). Rendered so the tab still answers "what is
                this product", which is half of why a user opens it. */}
            <div className="cat-fld">
              <span className="cat-fld-label">{t("catalogue.productDetail.details.kind")}</span>
              <p className="cat-fact">{t(`kind.${product.kind}`)}</p>
            </div>
            <div className="cat-fld">
              <span className="cat-fld-label">{t("catalogue.productDetail.details.baseUom")}</span>
              <p className="cat-fact">{uomCode.get(product.base_uom_id) ?? "—"}</p>
            </div>

            <div className="cat-fld">
              <label htmlFor="detail-make-or-buy">
                {t("catalogue.productDetail.details.makeOrBuy")}
              </label>
              <Select
                id="detail-make-or-buy"
                value={form.make_or_buy}
                disabled={disabled}
                onChange={(e) => {
                  set("make_or_buy", e.target.value as Product["make_or_buy"]);
                }}
              >
                {MAKE_OR_BUY.map((m) => (
                  <option key={m} value={m}>
                    {t(`catalogue.productsList.makeOrBuy.${m}`)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="cat-fld">
              <label htmlFor="detail-tracking">
                {t("catalogue.productDetail.details.tracking")}
              </label>
              <Select
                id="detail-tracking"
                value={form.tracking}
                disabled={disabled}
                onChange={(e) => {
                  set("tracking", e.target.value as Product["tracking"]);
                }}
              >
                {PRODUCT_TRACKING.map((k) => (
                  <option key={k} value={k}>
                    {t(`catalogue.tracking.${k}`)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="cat-fld">
              <label htmlFor="detail-scrap">
                {t("catalogue.productDetail.details.assemblyScrapPct")}
              </label>
              <input
                id="detail-scrap"
                type="number"
                min={0}
                max={99.99}
                step="0.01"
                value={form.assembly_scrap_pct}
                disabled={disabled}
                onChange={(e) => {
                  set("assembly_scrap_pct", e.target.value);
                }}
              />
            </div>

            <div className="cat-fld">
              <label htmlFor="detail-price-unit-qty">
                {t("catalogue.productDetail.details.priceUnitQty")}
              </label>
              <input
                id="detail-price-unit-qty"
                type="number"
                min={0}
                step="any"
                value={form.price_unit_qty}
                disabled={disabled}
                onChange={(e) => {
                  set("price_unit_qty", e.target.value);
                }}
              />
              <p className="cat-hint">{t("catalogue.productDetail.details.priceUnitQtyHint")}</p>
            </div>

            <div className="cat-fld">
              <label htmlFor="detail-purchase-lead">
                {t("catalogue.productDetail.details.purchaseLeadDays")}
              </label>
              <input
                id="detail-purchase-lead"
                type="number"
                min={0}
                step="1"
                value={form.purchase_lead_days}
                disabled={disabled}
                onChange={(e) => {
                  set("purchase_lead_days", e.target.value);
                }}
              />
            </div>

            <div className="cat-fld">
              <label htmlFor="detail-manufacture-lead">
                {t("catalogue.productDetail.details.manufactureLeadDays")}
              </label>
              <input
                id="detail-manufacture-lead"
                type="number"
                min={0}
                step="1"
                value={form.manufacture_lead_days}
                disabled={disabled}
                onChange={(e) => {
                  set("manufacture_lead_days", e.target.value);
                }}
              />
            </div>

            <div className="cat-fld">
              <label htmlFor="detail-facility">
                {t("catalogue.productDetail.details.defaultFacility")}
              </label>
              <Select
                id="detail-facility"
                value={form.default_facility_id}
                disabled={disabled}
                onChange={(e) => {
                  set("default_facility_id", e.target.value);
                }}
              >
                <option value="">{t("catalogue.productDetail.details.noFacility")}</option>
                {(facilities.data?.items ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {serverErrors.map((e, i) => (
            <p className="field-error field-error-server" role="alert" key={i}>
              {t(e.message_key, { ...(e.params ?? {}), defaultValue: e.message_key })}
            </p>
          ))}

          {canWrite ? (
            <div className="cat-row">
              {/* Primary only while there is something to commit — a save with
                  nothing pending is a no-op, and a no-op does not get to be the
                  loudest thing on the screen (§1 rule 1). */}
              <Button
                variant={dirty ? "primary" : "secondary"}
                data-testid="save-details"
                disabled={!dirty && !update.isPending}
                busy={update.isPending}
                busyLabel={t("catalogue.productDetail.details.saving")}
                onClick={save}
              >
                {t("catalogue.productDetail.details.save")}
              </Button>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

// ---- Attributes -------------------------------------------------------------

export function AttributesTab({
  product,
  onDirtyChange,
}: {
  product: Product;
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
}) {
  const { t } = useTranslation();
  const schemas = useAttributeSchemas();
  const update = useUpdateProduct(product.id);
  const perms = useCataloguePermissions();
  const canWrite = perms.has("catalogue.product.write");
  const [attributes, setAttributes] = useState<Record<string, unknown>>(product.attributes);
  const [serverErrors, setServerErrors] = useState<readonly FieldError[]>([]);
  const [notice, setNotice] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  const schema =
    product.attribute_schema_id !== null
      ? (schemas.data?.items ?? []).find(
          (s): s is AttributeSchema => s.id === product.attribute_schema_id,
        )
      : undefined;

  if (product.attribute_schema_id === null) {
    return (
      <div className="attributes-tab">
        <p className="tab-empty">{t("catalogue.productDetail.attributes.noSchema")}</p>
      </div>
    );
  }

  if (schemas.isPending)
    return (
      <div className="attributes-tab">
        <CardSkeleton label={t("catalogue.state.loading")} />
      </div>
    );

  if (schemas.error)
    return (
      <div className="attributes-tab">
        <TabError query={schemas} titleKey="catalogue.productDetail.attributes.error" />
      </div>
    );

  function save() {
    setNotice(null);
    setServerErrors([]);
    update.mutate(
      { attributes },
      {
        onSuccess: () => {
          setNotice({ kind: "ok", text: t("catalogue.productDetail.attributes.saved") });
          setDirty(false);
          onDirtyChange?.(false);
        },
        onError: (err: unknown) => {
          if (err instanceof RequestError && err.fieldErrors.length > 0) {
            setServerErrors(err.fieldErrors);
          }
          setNotice({
            kind: "bad",
            text:
              err instanceof Error ? err.message : t("catalogue.productDetail.attributes.error"),
          });
        },
      },
    );
  }

  return (
    <div className="attributes-tab">
      {notice !== null ? (
        <Banner
          kind={notice.kind}
          data-testid="attributes-result"
          dismissLabel={t("catalogue.state.dismiss")}
          onDismiss={() => {
            setNotice(null);
          }}
        >
          {notice.text}
        </Banner>
      ) : null}
      <Card title={t("catalogue.productDetail.tabs.attributes")}>
        <div className="ds-cpad">
          <SchemaAttributeForm
            jsonSchema={schema?.json_schema}
            uiHints={schema?.ui_hints}
            value={attributes}
            onChange={(next) => {
              setAttributes(next);
              setNotice(null);
              setDirty(true);
              onDirtyChange?.(true);
            }}
            serverErrors={serverErrors}
            disabled={update.isPending || !canWrite}
          />
          {/* Editing attributes needs catalogue.product.write (§7) — the Save
              control is hidden for read-only roles; the server re-validates. */}
          {canWrite ? (
            <div className="cat-row">
              {/* Primary only while dirty — see the note on Save details. */}
              <Button
                variant={dirty ? "primary" : "secondary"}
                disabled={!dirty && !update.isPending}
                busy={update.isPending}
                busyLabel={t("catalogue.productDetail.attributes.saving")}
                onClick={save}
              >
                {t("catalogue.productDetail.attributes.save")}
              </Button>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

// ---- UoM & conversions ------------------------------------------------------

export function UomTab({ product }: { product: Product }) {
  const { t } = useTranslation();
  const uomCode = useUomCodeMap();
  const conversions = useProductConversions(product.id);

  const columns: DataTableColumn<Conversion>[] = [
    {
      key: "from",
      header: t("catalogue.productDetail.uom.from"),
      id: true,
      render: (c) => uomCode.get(c.from_uom_id) ?? c.from_uom_id,
    },
    {
      key: "to",
      header: t("catalogue.productDetail.uom.to"),
      render: (c) => uomCode.get(c.to_uom_id) ?? c.to_uom_id,
    },
    { key: "factor", header: t("catalogue.productDetail.uom.factor"), numeric: true },
  ];

  return (
    <div className="uom-tab">
      <Card title={t("catalogue.productDetail.uom.conversionsTitle")}>
        <div className="ds-cpad">
          <p className="uom-base">
            <span className="uom-base-label">{t("catalogue.productDetail.uom.base")}</span>
            <strong>{uomCode.get(product.base_uom_id) ?? "—"}</strong>
          </p>
        </div>
        <DataTable
          columns={columns}
          rows={conversions.data?.items ?? []}
          rowKey={(c) => c.id}
          rowTestId="conversion-row"
          caption={t("catalogue.productDetail.uom.conversionsTitle")}
          loading={conversions.isPending}
          loadingLabel={t("catalogue.productDetail.uom.loading")}
          error={
            conversions.error ? (
              <TabError query={conversions} titleKey="catalogue.productDetail.uom.error" />
            ) : undefined
          }
          empty={<p className="tab-empty ds-cpad">{t("catalogue.productDetail.uom.none")}</p>}
        />
      </Card>
    </div>
  );
}

// ---- BOMs -------------------------------------------------------------------

export function BomsTab({ product }: { product: Product }) {
  const { t } = useTranslation();
  const boms = useProductBoms(product.id);
  const facilityName = useFacilityNameMap();

  if (boms.isPending)
    return (
      <div className="boms-tab">
        <CardSkeleton label={t("catalogue.productDetail.boms.loading")} />
      </div>
    );
  if (boms.error)
    return (
      <div className="boms-tab">
        <TabError query={boms} titleKey="catalogue.productDetail.boms.error" />
      </div>
    );

  const items = boms.data.items;
  if (items.length === 0)
    return (
      <div className="boms-tab">
        <p className="tab-empty">{t("catalogue.productDetail.boms.none")}</p>
      </div>
    );

  return (
    <div className="boms-tab">
      <Card title={t("catalogue.productDetail.tabs.boms")} count={items.length}>
        <div className="ds-cpad">
          <EffectivityTimeline versions={items} />
        </div>
        <ul className="version-list">
          {items.map((b) => (
            <li key={b.id} className="version-row">
              <span className="version-no">v{b.version}</span>
              <StatusBadge status={b.status} />
              {b.is_phantom ? (
                <span className="phantom-badge">{t("catalogue.productDetail.boms.phantom")}</span>
              ) : null}
              <span className="version-facility">
                {b.facility_id === null
                  ? t("catalogue.productDetail.boms.facilityWide")
                  : (facilityName.get(b.facility_id) ?? b.facility_id)}
              </span>
              <Link
                to="/catalogue/products/$id/boms/$bomId"
                params={{ id: product.id, bomId: b.id }}
                className="ds-lnk"
              >
                {t("catalogue.productDetail.boms.open")}
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ---- Routings ---------------------------------------------------------------

export function RoutingsTab({ product }: { product: Product }) {
  const { t } = useTranslation();
  const routings = useProductRoutings(product.id);
  const facilityName = useFacilityNameMap();

  if (routings.isPending)
    return (
      <div className="routings-tab">
        <CardSkeleton label={t("catalogue.productDetail.routings.loading")} />
      </div>
    );
  if (routings.error)
    return (
      <div className="routings-tab">
        <TabError query={routings} titleKey="catalogue.productDetail.routings.error" />
      </div>
    );

  const items = routings.data.items;
  if (items.length === 0)
    return (
      <div className="routings-tab">
        <p className="tab-empty">{t("catalogue.productDetail.routings.none")}</p>
      </div>
    );

  return (
    <div className="routings-tab">
      <Card title={t("catalogue.productDetail.tabs.routings")} count={items.length}>
        <ul className="version-list">
          {items.map((r) => (
            <li key={r.id} className="version-row">
              <span className="version-no">v{r.version}</span>
              <StatusBadge status={r.status} />
              <span className="version-facility">
                {t("catalogue.productDetail.routings.facility")}:{" "}
                {facilityName.get(r.facility_id) ?? r.facility_id}
              </span>
              <Link
                to="/catalogue/products/$id/routings/$routingId"
                params={{ id: product.id, routingId: r.id }}
                className="ds-lnk"
              >
                {t("catalogue.productDetail.routings.open")}
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ---- Where-used -------------------------------------------------------------

export function WhereUsedTab({ product }: { product: Product }) {
  const { t } = useTranslation();
  const whereUsed = useWhereUsed(product.id);

  if (whereUsed.isPending)
    return (
      <div className="where-used-tab">
        <CardSkeleton label={t("catalogue.productDetail.whereUsed.loading")} />
      </div>
    );
  if (whereUsed.error)
    return (
      <div className="where-used-tab">
        <TabError query={whereUsed} titleKey="catalogue.productDetail.whereUsed.error" />
      </div>
    );

  const asComponent = whereUsed.data.as_component;
  const asOutput = whereUsed.data.as_output;
  if (asComponent.length === 0 && asOutput.length === 0) {
    return (
      <div className="where-used-tab">
        <p className="tab-empty">{t("catalogue.productDetail.whereUsed.none")}</p>
      </div>
    );
  }

  return (
    <div className="where-used-tab">
      {asComponent.length > 0 ? (
        <WhereUsedList
          title={t("catalogue.productDetail.whereUsed.asComponent")}
          refs={asComponent}
        />
      ) : null}
      {asOutput.length > 0 ? (
        <WhereUsedList title={t("catalogue.productDetail.whereUsed.asOutput")} refs={asOutput} />
      ) : null}
    </div>
  );
}

function WhereUsedList({
  title,
  refs,
}: {
  title: string;
  refs: readonly components["schemas"]["WhereUsedRef"][];
}) {
  const { t } = useTranslation();
  return (
    <Card title={title} count={refs.length}>
      <ul className="version-list">
        {refs.map((ref) => (
          <li key={`${ref.bom_id}:${ref.product_id}`} className="version-row">
            <Link to="/catalogue/products/$id" params={{ id: ref.product_id }} className="ds-lnk">
              {ref.product_sku}
            </Link>
            <span className="version-no">
              {t("catalogue.productDetail.whereUsed.version", { version: ref.version })}
            </span>
            <StatusBadge status={ref.status} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---- Cost -------------------------------------------------------------------

export function CostTab({ product }: { product: Product }) {
  const { t } = useTranslation();
  return (
    <div className="cost-tab">
      <Card title={t("catalogue.productDetail.tabs.cost")}>
        <div className="ds-cpad">
          <p className="cost-headline">
            <span className="cost-headline-label">
              {t("catalogue.productDetail.cost.standard")}
            </span>
            {product.standard_cost_minor === null ? (
              <span className="cost cost-empty">{t("catalogue.productDetail.cost.notCosted")}</span>
            ) : (
              <CostValue
                minor={product.standard_cost_minor}
                currency={product.currency}
                priceUnitQty={product.price_unit_qty}
              />
            )}
          </p>
          {product.cost_run_id !== undefined && product.cost_run_id !== null ? (
            <p className="cost-run-ref">
              <span>{t("catalogue.productDetail.cost.run")}</span>
              <Link
                to="/catalogue/cost-runs/$id"
                params={{ id: product.cost_run_id }}
                className="ds-lnk"
              >
                {product.cost_run_id}
              </Link>
            </p>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

// ---- History ----------------------------------------------------------------

export function HistoryTab({ product }: { product: Product }) {
  const { t, i18n } = useTranslation();
  const changeLog = useChangeLog({ product_id: product.id });

  // THE TWO HISTORIES, SIDE BY SIDE AND NAMED APART.
  //
  // This tab shows the ECO change log — module 01's own record of BOM/routing
  // effectivity, with the engineering REASON on each row. It is not the audit
  // trail: it holds two entity types and only the changes this module tracks.
  // The platform-wide hash-chained `audit_log` holds every act performed on
  // this product — activation, discontinuation, price and cost-run releases,
  // attribute edits — and until spec 16's read surface existed there was no way
  // to see any of it. The link is rendered in EVERY branch below (including the
  // empty one, which is exactly when a reader most wants the wider record) and
  // renders nothing for a principal who cannot read the trail.
  const auditLink = (
    <p className="audit-entity-link-row">
      <AuditTrailLink entityType="product" entityId={product.id} />
    </p>
  );

  if (changeLog.isPending)
    return (
      <div className="history-tab">
        <CardSkeleton label={t("catalogue.productDetail.history.loading")} />
      </div>
    );
  if (changeLog.error)
    return (
      <div className="history-tab">
        <TabError query={changeLog} titleKey="catalogue.productDetail.history.error" />
        {auditLink}
      </div>
    );

  const items = changeLog.data.items;
  if (items.length === 0)
    return (
      <div className="history-tab">
        <p className="tab-empty">{t("catalogue.productDetail.history.none")}</p>
        {auditLink}
      </div>
    );

  return (
    <div className="history-tab">
      <Card title={t("catalogue.productDetail.tabs.history")} count={items.length}>
        <ul className="history-list">
          {items.map((entry) => (
            <li key={entry.id} className="history-row">
              <div className="history-row-main">
                <span className="history-entity">
                  {entry.entity_type === "bom"
                    ? t("catalogue.productDetail.history.entityBom")
                    : t("catalogue.productDetail.history.entityRouting")}
                </span>
                <span className="history-effective">
                  {t("catalogue.productDetail.history.effectiveFrom", {
                    date: entry.effective_from,
                  })}
                </span>
                <span className="history-reason">{entry.reason}</span>
              </div>
              {/* Audit slice (spec S2 "History = change log + audit slice"): the
                  change-log rows are hash-chained audit records — surface their
                  provenance (who + when) beside the engineering-change reason. */}
              <div className="history-audit">
                {t("catalogue.productDetail.history.recordedBy", {
                  date: formatTimestampDate(entry.created_at, i18n.language),
                  actor: shortActor(entry.actor_id),
                })}
              </div>
            </li>
          ))}
        </ul>
      </Card>
      {auditLink}
    </div>
  );
}

/** A short, human-scannable form of an actor uuid for the audit slice. */
function shortActor(id: string): string {
  return shortIdTail(id);
}
